import type { ComponentPropsWithoutRef, ReactElement } from 'react'
import { forwardRef } from 'react'

import { SlotProvider } from '@adverity/ds-core/slot'
import { cn } from '@adverity/ds-tokens/cn'
import { type VariantProps, cva } from '@adverity/ds-tokens/cva'

const spotIconVariants = cva('flex shrink-0 items-center justify-center rounded-round', {
    variants: {
        size: {
            'x-small': 'size-medium',
            small: 'size-large',
            medium: 'size-xx-large',
            large: 'size-xxx-large',
            'x-large': 'size-[72px]',
        },
        variant: {
            brand: 'bg-brand-secondary-active text-brand',
            neutral: 'bg-background-emphasis text-text-secondary',
            backgroundSecondary: 'bg-background-secondary text-brand-active',
            danger: 'bg-danger-secondary text-danger-highlighted',
            success: 'bg-success-secondary-highlighted text-success',
            warning: 'bg-warning-secondary-highlighted text-warning',
            metric: 'bg-metric-secondary-highlighted text-metric',
            dimension: 'bg-dimension-secondary-highlighted text-dimension',
        },
    },
    defaultVariants: {
        size: 'medium',
        variant: 'brand',
    },
})

type SpotIconVariants = VariantProps<typeof spotIconVariants>
type SpotIconSize = NonNullable<SpotIconVariants['size']>
type SpotIconVariant = NonNullable<SpotIconVariants['variant']>

const backgroundColorClassMap = {
    background: 'bg-background',
    backgroundContent: 'bg-background-content',
    backgroundSecondary: 'bg-background-secondary',
    backgroundEmphasis: 'bg-background-emphasis',
    brand: 'bg-brand',
    brandSecondary: 'bg-brand-secondary',
    brandSecondaryActive: 'bg-brand-secondary-active',
    brandSecondaryHighlighted: 'bg-brand-secondary-highlighted',
    dangerSecondary: 'bg-danger-secondary',
    dangerSecondaryHighlighted: 'bg-danger-secondary-highlighted',
    successSecondaryHighlighted: 'bg-success-secondary-highlighted',
    warningSecondaryHighlighted: 'bg-warning-secondary-highlighted',
    metricSecondaryHighlighted: 'bg-metric-secondary-highlighted',
    dimensionSecondaryHighlighted: 'bg-dimension-secondary-highlighted',
} as const

type SpotIconBackgroundColor = keyof typeof backgroundColorClassMap

type SpotIconProps = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & {
    size?: SpotIconSize
    variant?: SpotIconVariant
    backgroundColor?: SpotIconBackgroundColor
    children: ReactElement
}

const SpotIcon = forwardRef<HTMLDivElement, SpotIconProps>(
    ({ size = 'medium', variant = 'brand', backgroundColor, className, children, ...props }, ref) => (
        <SlotProvider slots={{ icon: { size } }}>
            <div
                ref={ref}
                className={cn(
                    spotIconVariants({ size, variant }),
                    backgroundColor && backgroundColorClassMap[backgroundColor],
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        </SlotProvider>
    ),
)

SpotIcon.displayName = 'SpotIcon'

export { SpotIcon, spotIconVariants }
export type { SpotIconProps, SpotIconVariants, SpotIconSize, SpotIconVariant, SpotIconBackgroundColor }
